import { watch } from 'vue'
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBackendVersion } from '@/composables/useBackendVersion'
import i18n from '@/locales'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Login', titleKey: 'nav.login', public: true, layout: 'blank' },
  },
  {
    // 初始化向导 (Round 10): 未初始化节点的首次引导, 守卫强制跳转
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/SetupWizardView.vue'),
    meta: { title: 'Setup', titleKey: 'nav.setup', public: true, layout: 'blank' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: 'Dashboard', titleKey: 'nav.dashboard', icon: 'LayoutDashboard' },
      },
      {
        path: 'tasks',
        name: 'tasks',
        component: () => import('@/views/TasksView.vue'),
        meta: { title: 'Tasks', titleKey: 'nav.tasks', icon: 'ListTodo' },
      },
      {
        // v3.17.4 任务详情 v2: 全屏视图 (菜单常驻), 从任务卡片点击进入
        path: 'tasks/detail/:gid',
        name: 'task-detail',
        component: () => import('@/views/TaskDetailView.vue'),
        meta: { title: 'Task Detail', titleKey: 'nav.tasks' },
      },
      {
        // v3.18.0 日志页: 服务端运行日志 + 审计日志 查看/过滤/下载
        path: 'logs',
        name: 'logs',
        component: () => import('@/views/LogsView.vue'),
        meta: { title: 'Logs', titleKey: 'nav.logs', icon: 'ScrollText' },
      },
      {
        path: 'files',
        name: 'files',
        component: () => import('@/views/FilesView.vue'),
        meta: { title: 'Files', titleKey: 'nav.files', icon: 'Folder' },
      },
      {
        path: 'cluster',
        name: 'cluster',
        component: () => import('@/views/ClusterView.vue'),
        meta: { title: 'Cluster', titleKey: 'nav.cluster', icon: 'Network' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { title: 'Settings', titleKey: 'nav.settings', icon: 'Settings' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Not Found', titleKey: 'nav.notFound', public: true },
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  auth.initFromStorage()

  // 初始化向导守卫 (Round 10): 账号未初始化 → 一律先进 /setup;
  // 已初始化 → /setup 不可再进。状态只查一次 (缓存在 auth store)。
  const configured = await auth.ensureAccountConfigured()
  if (!configured && to.name !== 'setup') {
    return { name: 'setup', query: { redirect: to.fullPath } }
  }
  if (configured && to.name === 'setup') {
    return { name: 'dashboard' }
  }

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  return true
})

// v3.19.22: 标签页标题改用「设置的服务器名字」(node.name → /api/system-info
// 的 node_name, 与登录卡副标题同源) — 多台服务器开多个标签页时一眼分清
// 是哪台 (如 "仪表盘 · SZ")。节点名为空时退回 "AFD Console",
// 绝不留下 "仪表盘 · " 这种带悬空分隔符的残缺标题。
//
// node_name 是异步取回的: 首次路由跳转时多半还没到, 所以 applyTitle
// 在 afterEach 与 nodeName watch 里各跑一次 — 谁后到谁定稿。
const { nodeName } = useBackendVersion()

function applyTitle(to: { meta: Record<string, unknown> }) {
  const base = nodeName.value || 'AFD Console'
  // v3.13.82: 标题走 i18n (titleKey), 修复顶栏/标签页一直显示英文的问题
  const key = to.meta.titleKey as string | undefined
  const fallback = (to.meta.title as string | undefined) ?? ''
  const title = key ? (i18n.global.t as (k: string) => string)(key) : fallback
  document.title = title ? `${title} · ${base}` : base
}

router.afterEach((to) => {
  applyTitle(to)
})

watch(nodeName, () => {
  applyTitle(router.currentRoute.value)
})

export default router
